import { useCallback, useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { auditService } from '../../services/auditService';
import { LocationUploader } from '../AuditPortal/LocationsPanel';
import MasterFileUploader from '../../components/MasterFileUploader';
import '../AuditPortal/ComparisonPanel.css';
import '../AuditPortal/LivePanels.css';
import './AuditSetupWizard.css';
import { PageLoader } from '../../components/Loader';
import ManpowerEditor from '../../components/Manpower/ManpowerEditor';

const STEPS = [
  { n: 1, title: 'Schedule details', hint: 'Client, store, dates, lead auditor' },
  { n: 2, title: 'Upload AMD (EANs)', hint: 'Article master the phones scan against' },
  { n: 3, title: 'Location master', hint: 'Racks / shelves to track zones' },
  { n: 4, title: 'User / manpower', hint: 'Teams and members on this schedule' },
];

const fmt = (value) => Number(value || 0).toLocaleString();

function when(iso) {
  if (!iso) return '—';
  try {
    return format(parseISO(iso), 'dd MMM yyyy, HH:mm');
  } catch {
    return iso;
  }
}

/**
 * Step-by-step audit setup: schedule (from the audit form), AMD upload,
 * location master and manpower. Each step shows whether it is done; optional
 * steps can be skipped. Book stock (ERP) is loaded later in the schedule
 * portal (Comparison tab).
 */
function AuditSetupWizard() {
  const { auditId } = useParams();
  const [params, setParams] = useSearchParams();
  const LAST = STEPS.length;
  const step = Math.min(LAST, Math.max(1, Number(params.get('step')) || 1));
  const goTo = (n) => setParams({ step: String(n) }, { replace: true });

  const [audit, setAudit] = useState(null);
  const [master, setMaster] = useState(null);
  const [locations, setLocations] = useState(null);
  const [teams, setTeams] = useState([]);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [showLocationUpload, setShowLocationUpload] = useState(false);

  const load = useCallback(async () => {
    try {
      const [auditData, masterMeta, locationData, teamData] = await Promise.all([
        auditService.getById(auditId),
        auditService.getMasterFile(auditId).catch(() => null),
        auditService.getLocations(auditId).catch(() => null),
        auditService.getTeams(auditId).catch(() => []),
      ]);
      setAudit(auditData);
      setMaster(masterMeta);
      setLocations(locationData);
      setTeams(teamData || []);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, [auditId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    setNotice('');
    setShowLocationUpload(false);
  }, [step]);

  const members = teams.flatMap((team) => team.members || []);
  const uniqueMembers = new Map(members.map((member) => [String(member.id), member]));

  const done = {
    1: Boolean(audit?.organization_id && audit?.outlet_id && audit?.start_at),
    2: Boolean(master?.row_count),
    3: Boolean(locations?.summary?.total_locations),
    4: uniqueMembers.size > 0,
  };

  if (!audit) {
    return (
      <div className="page setup">
        {error ? <p className="compare-error">{error}</p> : <PageLoader label="Loading schedule…" />}
      </div>
    );
  }

  const nav = (
    <footer className="setup__nav">
      {step > 1 ? (
        <button type="button" className="compare-button compare-button--ghost" onClick={() => goTo(step - 1)}>
          ← Back
        </button>
      ) : (
        <span />
      )}
      {step < LAST ? (
        <button type="button" className="compare-button" onClick={() => goTo(step + 1)}>
          {done[step] ? 'Next' : step === 1 ? 'Next' : 'Skip for now'}: {STEPS[step].title} →
        </button>
      ) : (
        <Link to={`/audits/${auditId}/portal`} className="compare-button setup__finish">
          Finish · open schedule portal →
        </Link>
      )}
    </footer>
  );

  return (
    <div className="page setup">
      <header className="setup__header">
        <div>
          <p className="page__eyebrow">Schedule setup</p>
          <h1>{audit.name}</h1>
          <p className="live-muted">
            {[audit.organization_name, audit.outlet_name ? `${audit.outlet_name}${audit.outlet_code ? ` (${audit.outlet_code})` : ''}` : '', audit.outlet_city]
              .filter(Boolean)
              .join(' · ') || 'No store chosen yet'}
            {' · '}
            {when(audit.start_at)} – {when(audit.end_at)}
          </p>
        </div>
        <Link to="/audits" className="audit-portal__back-link">
          Back to schedules
        </Link>
      </header>

      <ol className="setup__steps">
        {STEPS.map((item) => (
          <li key={item.n}>
            <button
              type="button"
              className={`setup__step${step === item.n ? ' setup__step--active' : ''}${done[item.n] ? ' setup__step--done' : ''}`}
              onClick={() => goTo(item.n)}
              aria-current={step === item.n ? 'step' : undefined}
            >
              <span className="setup__step-n">{done[item.n] ? `✓ 0${item.n}` : `0${item.n}`}</span>
              <strong>{item.title}</strong>
              <small>{item.hint}</small>
            </button>
          </li>
        ))}
      </ol>

      <section className="audit-portal__panel live-panel setup__body">
        <h2>
          Step {step}: {STEPS[step - 1].title}
        </h2>
        {error ? <p className="compare-error">{error}</p> : null}
        {notice ? <p className="live-notice">{notice}</p> : null}

        {step === 1 ? (
          <>
            <dl className="live-review">
              <div>
                <dt>Client account</dt>
                <dd>{audit.organization_name || '—'}</dd>
              </div>
              <div>
                <dt>Store / facility</dt>
                <dd>
                  {audit.outlet_name || '—'}
                  {audit.outlet_code ? ` (${audit.outlet_code})` : ''}
                  {audit.outlet_city ? ` · ${audit.outlet_city}` : ''}
                </dd>
              </div>
              <div>
                <dt>Start</dt>
                <dd>{when(audit.start_at)}</dd>
              </div>
              <div>
                <dt>Planned end</dt>
                <dd>{when(audit.end_at)}</dd>
              </div>
              <div>
                <dt>Lead stock auditor</dt>
                <dd>{audit.lead_auditor_name || 'Not set'}</dd>
              </div>
              <div>
                <dt>Client viewers</dt>
                <dd>Set on the Clients page by organization / outlet</dd>
              </div>
            </dl>
            <p>
              <Link to={`/audits?edit=${auditId}`} className="compare-link">
                Edit schedule details in the schedule form →
              </Link>
            </p>
          </>
        ) : null}

        {step === 2 ? (
          <>
            <p className="live-muted">
              The article master (AMD) as .txt, CSV, .xlsx or .xlsb. Choose which columns hold the
              EAN, article code and description. Team leads download it on the phones with “Sync
              AMD from Server”.
            </p>
            <p>
              {master?.row_count ? (
                <>
                  <span className="live-pill live-pill--good">Uploaded</span>{' '}
                  {master.original_filename || 'Master file'} · {fmt(master.row_count)} products
                  {master.uploaded_at ? ` · ${when(master.uploaded_at)}` : ''}
                </>
              ) : (
                <span className="live-pill live-pill--warn">Not uploaded yet</span>
              )}
            </p>
            <MasterFileUploader
              auditId={auditId}
              storeCode={audit.outlet_code || ''}
              onUploaded={async () => {
                setNotice('AMD uploaded.');
                await load();
              }}
            />
          </>
        ) : null}

        {step === 3 ? (
          <>
            <p className="live-muted">
              Optional but recommended: the list of racks / shelves / gondolas. The portal then
              shows how many locations are done, and phones get the list with the AMD.
            </p>
            <p>
              {locations?.summary?.total_locations ? (
                <>
                  <span className="live-pill live-pill--good">Uploaded</span>{' '}
                  {locations.meta?.original_filename || 'Location master'} · {fmt(locations.summary.total_locations)} locations
                  {locations.zones?.length ? ` in ${locations.zones.length} zones` : ''}
                </>
              ) : (
                <span className="live-pill live-pill--warn">Not uploaded yet</span>
              )}
            </p>
            {showLocationUpload ? (
              <LocationUploader
                auditId={auditId}
                onCancel={() => setShowLocationUpload(false)}
                onDone={async (result) => {
                  setShowLocationUpload(false);
                  setNotice(`Location master uploaded: ${fmt(result.row_count)} locations.`);
                  await load();
                }}
              />
            ) : (
              <button type="button" className="compare-button" onClick={() => setShowLocationUpload(true)}>
                {locations?.summary?.total_locations ? 'Replace location master' : 'Upload location master'}
              </button>
            )}
          </>
        ) : null}

        {step === 4 ? (
          <>
            <p className="live-muted">
              Who scans this schedule. Add people one by one or a whole team, choose the lead auditor, and mark
              anyone who is off or replaced on some days. Clashes with other schedules on the same dates are shown.
            </p>
            <ManpowerEditor auditId={auditId} layout="page" onSaved={load} />
          </>
        ) : null}

        {nav}
      </section>
    </div>
  );
}

export default AuditSetupWizard;
